import {useState} from 'react';
import {Layout} from '@/components/Layout';
import {StatCard} from '@/components/StatCard';
import {Zap, Battery, Clock, TrendingUp, AlertCircle, CheckCircle, Wrench, Users} from 'lucide-react';
import {Card} from '@/components/ui/card';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Progress} from '@/components/ui/progress';
import {LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell} from 'recharts';

const chargerStats = {
    total: 48,
    available: 32,
    inUse: 12,
    outOfOrder: 4,
    totalKwhDelivered: 15847,
    avgChargingTime: '45min',
    utilizationRate: 67
};

const utilizationData = [
    {hour: '00h', usage: 12},
    {hour: '04h', usage: 8},
    {hour: '08h', usage: 45},
    {hour: '12h', usage: 68},
    {hour: '16h', usage: 82},
    {hour: '20h', usage: 55},
    {hour: '24h', usage: 25}
];

const powerDistribution = [
    {name: '7kW', value: 12, color: '#3b82f6'},
    {name: '22kW', value: 20, color: '#10b981'},
    {name: '50kW', value: 10, color: '#f59e0b'},
    {name: '150kW', value: 6, color: '#ef4444'}
];

const topLocations = [
    {location: 'Shopping Colombo', chargers: 8, usage: 94, revenue: 1847},
    {location: 'Gare Oriente', chargers: 6, usage: 89, revenue: 1623},
    {location: 'Parque das Nações', chargers: 5, usage: 76, revenue: 1289},
    {location: 'Amoreiras Shopping', chargers: 4, usage: 71, revenue: 1156},
    {location: 'El Corte Inglés', chargers: 3, usage: 68, revenue: 982}
];

const recentCharges = [
    {id: 'CHG-8932', tag: 'TAG-00432', location: 'Shopping Colombo', power: '22kW', duration: '48min', kwh: 17.6, status: 'completed', user: 'João S.'},
    {id: 'CHG-8931', tag: 'TAG-00287', location: 'Gare Oriente', power: '50kW', duration: '32min', kwh: 26.7, status: 'in_progress', user: 'Maria C.'},
    {id: 'CHG-8930', tag: 'TAG-00519', location: 'Parque Nações', power: '7kW', duration: '2h 15min', kwh: 15.8, status: 'completed', user: 'Pedro M.'},
    {id: 'CHG-8929', tag: 'TAG-00341', location: 'Amoreiras', power: '150kW', duration: '18min', kwh: 45.0, status: 'completed', user: 'Ana R.'}
];

const maintenanceSchedule = [
    {tag: 'TAG-00432', location: 'Shopping Colombo', nextMaintenance: '2024-02-01', daysRemaining: 15, priority: 'normal'},
    {tag: 'TAG-00287', location: 'Gare Oriente', nextMaintenance: '2024-01-25', daysRemaining: 8, priority: 'high'},
    {tag: 'TAG-00892', location: 'Amoreiras', nextMaintenance: '2024-01-20', daysRemaining: 3, priority: 'urgent'}
];

export const ChargersDashboard = () => {
    const [period, setPeriod] = useState('today');

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Dashboard de Carregadores</h1>
                        <p className="text-slate-600 font-primary mt-1">Monitorização e gestão de postos de carregamento</p>
                    </div>
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger className="w-40">
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="today">Hoje</SelectItem>
                            <SelectItem value="week">Esta Semana</SelectItem>
                            <SelectItem value="month">Este Mês</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                        title="Total Carregadores"
                        value={chargerStats.total}
                        icon={Zap}
                        color="blue"
                    />
                    <StatCard
                        title="Disponíveis"
                        value={chargerStats.available}
                        change={`${((chargerStats.available / chargerStats.total) * 100).toFixed(0)}% da rede`}
                        changeType="neutral"
                        icon={CheckCircle}
                        color="emerald"
                    />
                    <StatCard
                        title="Em Uso"
                        value={chargerStats.inUse}
                        change={`${chargerStats.utilizationRate}% utilização`}
                        changeType="increase"
                        icon={Battery}
                        color="amber"
                    />
                    <StatCard
                        title="Avariados"
                        value={chargerStats.outOfOrder}
                        change="Requer atenção"
                        changeType="decrease"
                        icon={AlertCircle}
                        color="purple"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Card className="lg:col-span-2 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-slate-900 text-lg font-secondary font-bold">Taxa de Utilização por Hora</h2>
                                <p className="text-slate-600 text-sm font-primary">Padrão de uso ao longo do dia</p>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart data={utilizationData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                                <XAxis dataKey="hour" stroke="#64748b" fontSize={12}/>
                                <YAxis stroke="#64748b" fontSize={12}/>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px'
                                    }}
                                />
                                <Line type="monotone" dataKey="usage" stroke="#2563eb" strokeWidth={3} dot={{fill: '#2563eb', r: 4}}/>
                            </LineChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-slate-900 text-lg font-secondary font-bold">Distribuição por Potência</h2>
                                <p className="text-slate-600 text-sm font-primary">Tipos de carregadores</p>
                            </div>
                        </div>
                        <div className="flex justify-center mb-4">
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={powerDistribution}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {powerDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color}/>
                                        ))}
                                    </Pie>
                                    <Tooltip/>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="space-y-2">
                            {powerDistribution.map((item) => (
                                <div key={item.name} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{backgroundColor: item.color}}/>
                                        <span className="text-slate-700">{item.name}</span>
                                    </div>
                                    <span className="font-bold text-slate-900">{item.value} unidades</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-slate-900 text-lg font-secondary font-bold">Top Localizações</h2>
                                <p className="text-slate-600 text-sm font-primary">Por utilização e receita</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {topLocations.map((loc, idx) => (
                                <div key={loc.location} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                                                {idx + 1}
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-900">{loc.location}</p>
                                                <p className="text-xs text-slate-600">{loc.chargers} carregadores</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-secondary font-bold text-slate-900">€{loc.revenue}</p>
                                            <p className="text-xs text-slate-600">receita</p>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                                            <span>Taxa de utilização</span>
                                            <span className="font-bold">{loc.usage}%</span>
                                        </div>
                                        <Progress value={loc.usage} className="h-2"/>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-slate-900 text-lg font-secondary font-bold">Carregamentos Recentes</h2>
                                <p className="text-slate-600 text-sm font-primary">Últimas sessões</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {recentCharges.map((charge) => (
                                <div key={charge.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-blue-100 p-2 rounded-lg">
                                                <Zap className="text-blue-600" size={16}/>
                                            </div>
                                            <div>
                                                <p className="text-sm font-secondary font-bold text-slate-900">{charge.id}</p>
                                                <p className="text-xs text-slate-600">{charge.location}</p>
                                            </div>
                                        </div>
                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                            charge.status === 'completed'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-blue-100 text-blue-700'
                                        }`}>
                      {charge.status === 'completed' ? 'Completo' : 'Em Progresso'}
                    </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                        <div>
                                            <p className="text-slate-600">Potência</p>
                                            <p className="font-bold text-slate-900">{charge.power}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-600">Duração</p>
                                            <p className="font-bold text-slate-900">{charge.duration}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-600">Energia</p>
                                            <p className="font-bold text-slate-900">{charge.kwh} kWh</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-slate-900 text-lg font-secondary font-bold">Agenda de Manutenção Preventiva</h2>
                            <p className="text-slate-600 text-sm font-primary">Próximas manutenções agendadas</p>
                        </div>
                        <Wrench className="text-amber-600" size={24}/>
                    </div>
                    <div className="space-y-3">
                        {maintenanceSchedule.map((item) => (
                            <div key={item.tag} className={`p-4 rounded-lg border-2 ${
                                item.priority === 'urgent' ? 'bg-red-50 border-red-300' :
                                    item.priority === 'high' ? 'bg-amber-50 border-amber-300' :
                                        'bg-slate-50 border-slate-200'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                                            item.priority === 'urgent' ? 'bg-red-200 text-red-800' :
                                                item.priority === 'high' ? 'bg-amber-200 text-amber-800' :
                                                    'bg-slate-200 text-slate-800'
                                        }`}>
                                            {item.priority === 'urgent' ? '🚨 Urgente' :
                                                item.priority === 'high' ? '⚠️ Alta' : '✓ Normal'}
                                        </div>
                                        <div>
                                            <p className="font-secondary font-bold text-slate-900">{item.tag}</p>
                                            <p className="text-sm text-slate-600">{item.location}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-slate-900">{item.nextMaintenance}</p>
                                        <p className="text-xs text-slate-600">Em {item.daysRemaining} dias</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-blue-50 p-3 rounded-lg">
                                <TrendingUp className="text-blue-600" size={24}/>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Total kWh Fornecidos</p>
                                <p className="text-2xl font-secondary font-bold text-slate-900">{chargerStats.totalKwhDelivered.toLocaleString()}</p>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500">Este mês</p>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-emerald-50 p-3 rounded-lg">
                                <Clock className="text-emerald-600" size={24}/>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Tempo Médio de Carregamento</p>
                                <p className="text-2xl font-secondary font-bold text-slate-900">{chargerStats.avgChargingTime}</p>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500">Últimos 30 dias</p>
                    </Card>

                    <Card className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-purple-50 p-3 rounded-lg">
                                <Users className="text-purple-600" size={24}/>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Taxa de Utilização</p>
                                <p className="text-2xl font-secondary font-bold text-slate-900">{chargerStats.utilizationRate}%</p>
                            </div>
                        </div>
                        <Progress value={chargerStats.utilizationRate} className="h-2"/>
                    </Card>
                </div>
            </div>
        </Layout>
    );
};
